import { cn } from '@latitude/token-utils';
import type { ElementType } from 'react';

import { Box, type BoxProps } from './Box';
import { GAP, type SpacingStep } from './spacing';

type InlineAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type InlineJustify =
  | 'start'
  | 'center'
  | 'end'
  | 'between'
  | 'around'
  | 'evenly';

const ALIGN: Record<InlineAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const JUSTIFY: Record<InlineJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

type InlineOwnProps = {
  gap?: SpacingStep;
  align?: InlineAlign;
  justify?: InlineJustify;
};

// Public API (design.md scope: Stack/Inline/Grid/GridItem). Horizontal flow
// that WRAPS — for tag rows, chip rows, button clusters. The wrap intent is
// visible in the component name, in contrast to Stack which never wraps.
export type InlineProps<E extends ElementType = 'div'> = InlineOwnProps &
  Omit<BoxProps<E>, keyof InlineOwnProps>;

export const Inline = <E extends ElementType = 'div'>({
  gap,
  align,
  justify,
  className,
  ...rest
}: InlineProps<E>) => {
  return (
    // @ts-expect-error - see Center.tsx for why: TS can't reduce two
    // independently-computed Omit<ComponentPropsWithoutRef<E>, ...>
    // expressions to prove they're structurally identical when E is an
    // unresolved generic, even though InlineProps<E> is defined in terms of
    // BoxProps<E>.
    <Box<E>
      className={cn(
        'flex flex-row flex-wrap',
        align !== undefined && ALIGN[align],
        justify !== undefined && JUSTIFY[justify],
        gap !== undefined && GAP[gap],
        className,
      )}
      {...rest}
    />
  );
};

Inline.displayName = 'Inline';
